import { getRoomGuest } from '../activeSession';

/**
 * The correct-sender check (Mark, 2026-10-21): the host takes a guest's name and
 * contact only from the room's recorded guest. A third phone that opened the
 * host's link while the room was still open could write into it (042 lets
 * anyone holding a live QR link write); the server keeps the first guest as the
 * room's guest, and now the host's record does too.
 *
 * Sender ids are server-verified (guard_chat_message: an anon sender must be
 * owned by the writing session), so comparing them is meaningful.
 *
 * Decides only between two anon ids: once the room's guest has converted, the
 * recorded id is their profile and their own messages arrive as
 * `upgraded_user` — not checked here. No recorded guest yet, or a failed read
 * (logged as `room_guest_read`): the message is taken, so a slow read never
 * drops the real guest's name — the room is closed to newcomers at the scan,
 * so this is the second layer, not the only one.
 */
const known = new Map();

const recordedGuest = (chatRoomId) => {
  if (!known.has(chatRoomId)) {
    // One read per room, shared by every message waiting on it, so messages
    // from the same guest are applied in the order they arrived
    const read = getRoomGuest(chatRoomId).then((id) => {
      if (!id) known.delete(chatRoomId); // read again next time
      return id;
    });
    known.set(chatRoomId, read);
  }
  return known.get(chatRoomId);
};

const isAnon = (id) => typeof id === 'string' && id.startsWith('anon_');

/**
 * @param {string} chatRoomId
 * @param {string|null|undefined} senderId - the message's server-verified sender
 * @returns {Promise<boolean>} false only when an anon sender is not the room's anon guest
 */
export const fromRecordedGuest = async (chatRoomId, senderId) => {
  if (!isAnon(senderId)) return true;
  const guest = await recordedGuest(chatRoomId);
  if (!isAnon(guest)) return true;
  return guest === senderId;
};

/** Tests only */
export const resetRoomGuests = () => known.clear();
